/**
 * SessionManagement Component
 *
 * Displays active sessions and allows users to revoke them.
 * Shows device info, location, last activity, and revocation controls.
 *
 * Epic: MVP 1.21.5 - Dashboard Completion
 * Story: SCRUM-528 - Guide Settings Backend (5 SP)
 * Location: src/components/account/SessionManagement.jsx
 */

import React, { useEffect, useState } from 'react';
import { Monitor, Smartphone, Tablet, MapPin as _MapPin, Clock, Trash2, Loader2, AlertCircle, MapPin } from 'lucide-react';
import apiClient from '../../utils/apiClient';
import ConfirmModal from '../shared/ConfirmModal';
import { logError } from '../../services/errorLoggingService';

export function SessionManagement() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [revoking, setRevoking] = useState(null);
  const [revokingAll, setRevokingAll] = useState(false);
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, type: null, sessionId: null });
  const [successMessage, setSuccessMessage] = useState(null);

  useEffect(() => {
    fetchSessions();
  }, []);

  const fetchSessions = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiClient.get('/guides/settings/sessions');

      if (response.data.success) {
        setSessions(response.data.data || []);
      }
    } catch (err) {
      console.error('Error fetching sessions:', err);
      setError(err.message || 'Failed to load sessions');
    } finally {
      setLoading(false);
    }
  };

  const handleRevokeSession = async (sessionId) => {
    try {
      setRevoking(sessionId);

      const response = await apiClient.delete(`/guides/settings/sessions/${sessionId}`);

      if (response.data.success) {
        // Remove from local state
        setSessions(sessions.filter(s => s.id !== sessionId));
        setSuccessMessage('Session revoked successfully');
        setTimeout(() => setSuccessMessage(null), 3000);
      }
    } catch (err) {
      console.error('Error revoking session:', err);
      logError(err, {
        componentName: 'SessionManagement',
        action: 'revoke_session',
        sessionId
      });
      setError('Failed to revoke session');
    } finally {
      setRevoking(null);
    }
  };

  const handleRevokeAllSessions = async () => {
    try {
      setRevokingAll(true);

      const response = await apiClient.delete('/guides/settings/sessions/all');

      if (response.data.success) {
        // Refresh sessions list
        await fetchSessions();
        setSuccessMessage(`${response.data.data.revokedCount} session(s) revoked successfully`);
        setTimeout(() => setSuccessMessage(null), 3000);
      }
    } catch (err) {
      console.error('Error revoking all sessions:', err);
      logError(err, {
        componentName: 'SessionManagement',
        action: 'revoke_all_sessions'
      });
      setError('Failed to revoke all sessions');
    } finally {
      setRevokingAll(false);
    }
  };

  const getDeviceIcon = (deviceInfo) => {
    const info = deviceInfo?.toLowerCase() || '';
    if (info.includes('mobile') || info.includes('android') || info.includes('iphone')) {
      return <Smartphone size={20} />;
    }
    if (info.includes('tablet') || info.includes('ipad')) {
      return <Tablet size={20} />;
    }
    return <Monitor size={20} />;
  };

  const formatLastActivity = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) {return 'Active now';}
    if (diffMins < 60) {return `${diffMins}m ago`;}
    if (diffHours < 24) {return `${diffHours}h ago`;}
    if (diffDays < 7) {return `${diffDays}d ago`;}

    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  if (loading) {
    return (
      <div style={{
        backgroundColor: 'var(--color-white)',
        borderRadius: 'var(--radius-lg)',
        padding: 'var(--space-6)',
        boxShadow: 'var(--shadow-md)',
        border: '1px solid var(--color-border-light)'
      }}>
        <h3 style={{
          fontSize: 'var(--font-size-lg)',
          fontWeight: 'var(--font-weight-semibold)',
          color: 'var(--color-text-primary)',
          marginBottom: 'var(--space-4)'
        }}>
          Active Sessions
        </h3>
        <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-8)' }}>
          <Loader2 size={32} style={{ animation: 'spin 1s linear infinite', color: 'var(--color-primary)' }} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        backgroundColor: 'var(--color-white)',
        borderRadius: 'var(--radius-lg)',
        padding: 'var(--space-6)',
        boxShadow: 'var(--shadow-md)',
        border: '1px solid var(--color-border-light)'
      }}>
        <h3 style={{
          fontSize: 'var(--font-size-lg)',
          fontWeight: 'var(--font-weight-semibold)',
          color: 'var(--color-text-primary)',
          marginBottom: 'var(--space-4)'
        }}>
          Active Sessions
        </h3>
        <div style={{
          padding: 'var(--space-4)',
          backgroundColor: 'var(--color-error-light)',
          borderRadius: 'var(--radius-md)',
          color: 'var(--color-error-dark)',
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-2)'
        }}>
          <AlertCircle size={20} />
          <span>{error}</span>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      backgroundColor: 'var(--color-white)',
      borderRadius: 'var(--radius-lg)',
      padding: 'var(--space-6)',
      boxShadow: 'var(--shadow-md)',
      border: '1px solid var(--color-border-light)'
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 'var(--space-4)'
      }}>
        <div>
          <h3 style={{
            fontSize: 'var(--font-size-lg)',
            fontWeight: 'var(--font-weight-semibold)',
            color: 'var(--color-text-primary)',
            marginBottom: 'var(--space-1)',
            margin: 0
          }}>
            Active Sessions
          </h3>
          <p style={{
            fontSize: 'var(--font-size-sm)',
            color: 'var(--color-text-secondary)',
            margin: 0
          }}>
            Manage devices where you&apos;re currently logged in
          </p>
        </div>
        {sessions.length > 1 && (
          <button
            onClick={() => setConfirmModal({ isOpen: true, type: 'all', sessionId: null })}
            disabled={revokingAll}
            style={{
              padding: 'var(--space-2) var(--space-4)',
              backgroundColor: revokingAll ? 'var(--color-gray-300)' : 'var(--color-error)',
              color: 'var(--color-white)',
              border: 'none',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 'var(--font-weight-medium)',
              cursor: revokingAll ? 'not-allowed' : 'pointer',
              transition: 'all var(--transition-fast)'
            }}
          >
            {revokingAll ? 'Revoking...' : 'Revoke All Others'}
          </button>
        )}
      </div>

      {/* Sessions List */}
      {sessions.length === 0 ? (
        <div style={{
          padding: 'var(--space-8)',
          textAlign: 'center',
          color: 'var(--color-text-secondary)',
          fontSize: 'var(--font-size-sm)'
        }}>
          No active sessions found
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
          {sessions.map((session) => (
            <div
              key={session.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-4)',
                padding: 'var(--space-4)',
                borderRadius: 'var(--radius-md)',
                border: `2px solid ${session.is_current ? 'var(--color-success)' : 'var(--color-border-light)'}`,
                backgroundColor: session.is_current ? 'var(--color-success-light)' : 'var(--color-white)'
              }}
            >
              {/* Device Icon */}
              <div style={{
                minWidth: '48px',
                height: '48px',
                borderRadius: 'var(--radius-md)',
                backgroundColor: session.is_current ? 'var(--color-success)' : 'var(--color-gray-100)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: session.is_current ? 'var(--color-white)' : 'var(--color-text-secondary)'
              }}>
                {getDeviceIcon(session.device_info)}
              </div>

              {/* Session Info */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 'var(--font-weight-semibold)',
                  color: 'var(--color-text-primary)',
                  marginBottom: 'var(--space-1)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-2)'
                }}>
                  {session.device_info || 'Unknown Device'}
                  {session.is_current && (
                    <span style={{
                      padding: '2px 8px',
                      backgroundColor: 'var(--color-success)',
                      color: 'var(--color-white)',
                      fontSize: 'var(--font-size-xs)',
                      fontWeight: 'var(--font-weight-medium)',
                      borderRadius: 'var(--radius-sm)'
                    }}>
                      Current
                    </span>
                  )}
                </div>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-4)',
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--color-text-secondary)'
                }}>
                  {session.ip_address && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)' }}>
                      <MapPin size={14} />
                      {session.ip_address}
                    </div>
                  )}
                  {session.last_activity && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)' }}>
                      <Clock size={14} />
                      {formatLastActivity(session.last_activity)}
                    </div>
                  )}
                </div>
              </div>

              {/* Revoke Button */}
              {!session.is_current && (
                <button
                  onClick={() => setConfirmModal({ isOpen: true, type: 'single', sessionId: session.id })}
                  disabled={revoking === session.id}
                  style={{
                    padding: 'var(--space-2) var(--space-3)',
                    backgroundColor: 'transparent',
                    color: revoking === session.id ? 'var(--color-gray-400)' : 'var(--color-error)',
                    border: `1px solid ${revoking === session.id ? 'var(--color-gray-400)' : 'var(--color-error)'}`,
                    borderRadius: 'var(--radius-md)',
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 'var(--font-weight-medium)',
                    cursor: revoking === session.id ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-1)',
                    transition: 'all var(--transition-fast)'
                  }}
                  onMouseOver={(  _e) => {
                    if (revoking !== session.id) {
                      _e.currentTarget.style.backgroundColor = 'var(--color-error)';
                      _e.currentTarget.style.color = 'var(--color-white)';
                    }
                  }}
                  onMouseOut={(  _e) => {
                    if (revoking !== session.id) {
                      _e.currentTarget.style.backgroundColor = 'transparent';
                      _e.currentTarget.style.color = 'var(--color-error)';
                    }
                  }}
                >
                  {revoking === session.id ? (
                    <>
                      <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
                      Revoking...
                    </>
                  ) : (
                    <>
                      <Trash2 size={14} />
                      Revoke
                    </>
                  )}
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Success Message */}
      {successMessage && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          backgroundColor: 'var(--color-success)',
          color: 'var(--color-white)',
          padding: 'var(--space-4)',
          borderRadius: 'var(--radius-md)',
          boxShadow: 'var(--shadow-lg)',
          zIndex: 1000
        }}>
          {successMessage}
        </div>
      )}

      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ isOpen: false, type: null, sessionId: null })}
        onConfirm={() => {
          if (confirmModal.type === 'single') {
            handleRevokeSession(confirmModal.sessionId);
          } else if (confirmModal.type === 'all') {
            handleRevokeAllSessions();
          }
          setConfirmModal({ isOpen: false, type: null, sessionId: null });
        }}
        title={confirmModal.type === 'all' ? 'Revoke All Sessions' : 'Revoke Session'}
        message={
          confirmModal.type === 'all'
            ? 'Are you sure you want to revoke all other sessions? You will be logged out on all other devices.'
            : 'Are you sure you want to revoke this session? You will be logged out on that device.'
        }
        confirmText="Revoke"
        confirmButtonClass="bg-red-600 hover:bg-red-700"
      />
    </div>
  );
}

export default SessionManagement;
